import {
  Button,
  ConfirmDialog,
  DialogTrigger,
  Icon,
  ProgressCircle,
  Thumbnail,
} from '@iconik-io/uad';

import styles from './comment_attachment_tiles.module.css';

export type CommentAttachmentTileItem = {
  id: string;
  name: string;
  url?: string | null;
  isUploading?: boolean;
};

type CommentAttachmentTilesProps = {
  items: CommentAttachmentTileItem[];
  onOpen?: (item: CommentAttachmentTileItem) => void;
  onRemove?: (item: CommentAttachmentTileItem) => void;
};

export function CommentAttachmentTiles({
  items,
  onOpen,
  onRemove,
}: CommentAttachmentTilesProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ul className={styles.tiles} aria-label="Attachments">
      {items.map((item) => (
        <CommentAttachmentTile
          key={item.id}
          item={item}
          onOpen={onOpen}
          onRemove={onRemove}
        />
      ))}
    </ul>
  );
}

type CommentAttachmentTileProps = {
  item: CommentAttachmentTileItem;
  onOpen?: (item: CommentAttachmentTileItem) => void;
  onRemove?: (item: CommentAttachmentTileItem) => void;
};

function CommentAttachmentTile({
  item,
  onOpen,
  onRemove,
}: CommentAttachmentTileProps) {
  const thumbnail = (
    <Thumbnail size="xl" url={item.url ?? undefined} alt={item.name}>
      {item.isUploading ? (
        <span className={styles.uploading}>
          <ProgressCircle size="s" aria-label={`Uploading ${item.name}`} />
        </span>
      ) : null}
    </Thumbnail>
  );

  return (
    <li className={styles.tile}>
      {onOpen && !item.isUploading ? (
        <Button
          className={styles.open_button}
          variant="tertiary"
          aria-label={`Open ${item.name}`}
          onPress={() => onOpen(item)}
        >
          {thumbnail}
        </Button>
      ) : (
        thumbnail
      )}
      {onRemove && !item.isUploading ? (
        <DialogTrigger>
          <Button
            className={styles.remove_button}
            size="s"
            variant="secondary"
            round
            icon={<Icon name="close" />}
            aria-label={`Remove ${item.name}`}
          />
          <ConfirmDialog
            title="Do you really want to delete the attachment?"
            confirmButtonLabel="Delete"
            cancelButtonLabel="Cancel"
            variant="destructive"
            onConfirm={() => onRemove(item)}
          />
        </DialogTrigger>
      ) : null}
    </li>
  );
}
